import React, { useState, useEffect, useRef } from 'react';
import { X, User, Lock, Upload, RefreshCw, CheckCircle } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from './LoadingSpinner';

const AttachmentReplaceModal = ({
  isOpen,
  onClose,
  attachment,
  invoiceId,
  users = [],
  defaultSignerUserId = null,
  onSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [signerUserId, setSignerUserId] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [challengeId, setChallengeId] = useState(null);
  const [creatingChallenge, setCreatingChallenge] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [totpSecondsRemaining, setTotpSecondsRemaining] = useState(null);
  const [totpPeriodSeconds, setTotpPeriodSeconds] = useState(30);
  const totpPeriodRef = useRef(30);
  const serverOffsetMsRef = useRef(0);

  const updateTotpTick = () => {
    const serverEpochSec = Math.floor((Date.now() + serverOffsetMsRef.current) / 1000);
    const period = totpPeriodRef.current;
    const intoWindow = serverEpochSec % period;
    const remaining = period - intoWindow;
    setTotpSecondsRemaining(remaining === period ? period : remaining);
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setSignerUserId(defaultSignerUserId ? String(defaultSignerUserId) : '');
      setUserSearchQuery('');
      setMfaCode('');
      setChallengeId(null);
      return;
    }
    setSignerUserId(defaultSignerUserId ? String(defaultSignerUserId) : '');
  }, [isOpen, defaultSignerUserId]);

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    updateTotpTick();

    const syncFromServer = async () => {
      try {
        const response = await api.get('/mfa/totp/clock');
        if (cancelled) return;
        const { serverTimeMillis, secondsRemaining, periodSeconds } = response.data;
        const period = periodSeconds || 30;
        totpPeriodRef.current = period;
        serverOffsetMsRef.current = serverTimeMillis - Date.now();
        setTotpSecondsRemaining(secondsRemaining);
        setTotpPeriodSeconds(period);
      } catch (err) {
        console.warn('Nie udało się pobrać zegara TOTP:', err);
      }
    };

    syncFromServer();
    const resyncInterval = setInterval(syncFromServer, 60000);
    const tickInterval = setInterval(updateTotpTick, 1000);

    return () => {
      cancelled = true;
      clearInterval(resyncInterval);
      clearInterval(tickInterval);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !attachment?.id || !signerUserId) {
      setChallengeId(null);
      return undefined;
    }

    let cancelled = false;

    const createChallenge = async () => {
      setCreatingChallenge(true);
      setChallengeId(null);
      try {
        const response = await api.post(
          `/invoices/attachments/${attachment.id}/replace/challenge`,
          { signerUserId: parseInt(signerUserId, 10) }
        );
        if (!cancelled) {
          setChallengeId(response.data.challengeId);
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(error.response?.data?.error || 'Nie udało się przygotować podpisu MFA');
        }
      } finally {
        if (!cancelled) {
          setCreatingChallenge(false);
        }
      }
    };

    createChallenge();
    return () => {
      cancelled = true;
    };
  }, [isOpen, attachment?.id, signerUserId]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.includes('.')
      ? file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
      : '';
    const allowed = ['.pdf', '.png', '.jpg', '.jpeg'];
    if (!allowed.includes(ext)) {
      toast.error('Dozwolone formaty: PDF, PNG, JPG');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Maksymalny rozmiar pliku: 10MB');
      return;
    }
    setSelectedFile(file);
  };

  const handleReplace = async () => {
    if (!selectedFile) {
      toast.error('Wybierz nowy plik załącznika');
      return;
    }
    if (!signerUserId) {
      toast.error('Wybierz użytkownika, który podpisze załącznik');
      return;
    }
    if (!challengeId) {
      toast.error('Oczekiwanie na przygotowanie MFA – spróbuj ponownie za chwilę');
      return;
    }
    if (!mfaCode || mfaCode.length !== 6) {
      toast.error('Wprowadź 6-cyfrowy kod z aplikacji Authenticator wybranego użytkownika');
      return;
    }

    setReplacing(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('signerUserId', signerUserId);
      formData.append('challengeId', challengeId);
      formData.append('code', mfaCode);

      await api.post(`/invoices/attachments/${attachment.id}/replace`, formData);
      toast.success('Załącznik został podmieniony i podpisany');
      onSuccess?.(invoiceId);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Błąd podczas podmiany załącznika');
    } finally {
      setReplacing(false);
    }
  };

  const filteredUsers = users.filter((u) => {
    if (u.active === false) return false;
    if (!userSearchQuery) return true;
    const query = userSearchQuery.toLowerCase();
    const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
    const email = (u.email || '').toLowerCase();
    return fullName.includes(query) || email.includes(query);
  });

  const selectedSigner = users.find((u) => String(u.id) === signerUserId);

  if (!isOpen || !attachment) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm p-3 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full border border-gray-200 max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-blue-600" />
              Podmień załącznik
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          <p className="text-xs sm:text-sm text-gray-600 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
            Aktualny plik: <strong>{attachment.originalFileName}</strong>
            <br />
            <span className="text-gray-500 mt-1 inline-block">
              Plik zostanie podmieniony (bez dodawania nowego). Wybrany użytkownik musi podać kod z Authenticatora.
            </span>
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                Nowy plik <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  id="attachment-replace-file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label
                  htmlFor="attachment-replace-file"
                  className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 flex-1"
                >
                  <Upload className="h-4 w-4 text-blue-600" />
                  <span className="truncate text-gray-700">
                    {selectedFile ? selectedFile.name : 'Wybierz plik (PDF, PNG, JPG)'}
                  </span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                Podpisze użytkownik <span className="text-red-500">*</span>
              </label>
              <div className="relative mb-2">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  placeholder="Wyszukaj użytkownika..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="border border-gray-200 rounded-lg max-h-36 overflow-y-auto">
                {filteredUsers.map((u) => (
                  <div
                    key={u.id}
                    onClick={() => setSignerUserId(String(u.id))}
                    className={`px-3 py-2 cursor-pointer hover:bg-blue-50 transition-colors ${
                      signerUserId === String(u.id) ? 'bg-blue-100 border-l-4 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email}
                        </p>
                        {u.email && <p className="text-xs text-gray-500">{u.email}</p>}
                      </div>
                      {signerUserId === String(u.id) && (
                        <CheckCircle className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                  </div>
                ))}
                {filteredUsers.length === 0 && (
                  <div className="px-3 py-4 text-center text-sm text-gray-500">
                    Brak użytkowników
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                Kod Authenticator {selectedSigner ? `(${selectedSigner.firstName} ${selectedSigner.lastName})` : ''}{' '}
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={creatingChallenge || !challengeId}
                />
              </div>
              {totpSecondsRemaining != null && (
                <p className="text-xs text-gray-500 mt-1">
                  Kod ważny jeszcze {totpSecondsRemaining}s (okno {totpPeriodSeconds}s)
                </p>
              )}
              {creatingChallenge && (
                <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                  <LoadingSpinner size="sm" />
                  Przygotowywanie weryfikacji MFA...
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4 mt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Anuluj
            </button>
            <button
              onClick={handleReplace}
              disabled={replacing || creatingChallenge || !challengeId}
              className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {replacing ? <LoadingSpinner size="sm" /> : <RefreshCw className="h-4 w-4" />}
              <span>Podmień i podpisz</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachmentReplaceModal;
